import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Trash2, Plus, Loader2, Check, CreditCard, Wallet as WalletIcon, Send, Download, History, Building2, Lock } from "lucide-react";
import VirtualCard from "./VirtualCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUsd = (n) => n == null ? "—" : Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const WALLET_TABS = [
  { k: "overview", l: "Overview", icon: WalletIcon },
  { k: "receive", l: "Receive", icon: Download },
  { k: "send", l: "Send", icon: Send },
  { k: "addresses", l: "Addresses", icon: WalletIcon },
  { k: "bank", l: "Bank Transfer", icon: Building2 },
  { k: "history", l: "History", icon: History },
  { k: "cards", l: "Cards", icon: CreditCard },
];

export default function Wallet({ token, account, profile }) {
  const [sub, setSub] = useState("overview");
  const headers = { Authorization: `Bearer ${token}` };
  const [overview, setOverview] = useState(null);
  const [networks, setNetworks] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/wallet/overview`, { headers }).then(({ data }) => setOverview(data)).catch(() => {});
    axios.get(`${API}/wallet/networks`).then(({ data }) => setNetworks(data.networks || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refreshKey]);

  return (
    <div className="mt-8 space-y-5" data-testid="wallet-section">
      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-3" data-testid="wallet-subtabs">
        {WALLET_TABS.map((s) => {
          const Icon = s.icon;
          const active = sub === s.k;
          return (
            <button
              key={s.k}
              data-testid={`wallet-tab-${s.k}`}
              onClick={() => setSub(s.k)}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors ${
                active ? "bg-gold text-ink" : "border border-white/10 text-white/60 hover:border-gold hover:text-gold"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {s.l}
            </button>
          );
        })}
      </div>

      {sub === "overview" && <WalletOverview overview={overview} account={account} />}
      {sub === "receive" && <WalletReceive networks={networks} />}
      {sub === "send" && <WalletSend token={token} overview={overview} onDone={reload} />}
      {sub === "addresses" && <WalletAddresses token={token} networks={networks} onChange={reload} />}
      {sub === "bank" && <WalletBank token={token} overview={overview} onChange={reload} />}
      {sub === "history" && <WalletHistory token={token} refreshKey={refreshKey} />}
      {sub === "cards" && <WalletCards overview={overview} account={account} profile={profile} />}
    </div>
  );
}

// ─── Overview ────────────────────────────────────────────────────────────
function WalletOverview({ overview, account }) {
  const bal = overview?.balance_usd ?? account?.balance_usd ?? 0;
  const seed = overview?.seed_capital ?? account?.seed_capital ?? 0;
  const tier = overview?.tier_name || "—";
  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="wallet-overview">
      <div className="rounded-2xl border border-gold/40 bg-black/40 p-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Aureon Wallet · USD</div>
        <div className="mt-2 font-serif text-4xl text-gold">${fmtUsd(bal)}</div>
        <div className="mt-1 text-xs text-white/50">Seed capital: ${fmtUsd(seed)}</div>
      </div>
      <StatCard label="Current tier" value={String(tier).toUpperCase()} testid="wallet-tier" />
      <StatCard label="Saved external addresses" value={overview?.saved_addresses ?? 0} testid="wallet-saved" />
      <StatCard label="Pending USDT withdrawals" value={overview?.pending_withdrawals ?? 0} testid="wallet-pending-wd" />
      <StatCard label="Pending bank transfers" value={overview?.pending_bank_transfers ?? 0} testid="wallet-pending-bank" />
      <StatCard label="On-file TRC-20" value={overview?.trc_wallet ? `${overview.trc_wallet.slice(0,6)}…${overview.trc_wallet.slice(-4)}` : "Not set"} testid="wallet-trc-set" />
    </div>
  );
}
function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">{label}</div>
      <div className="mt-2 font-serif text-2xl text-white">{value}</div>
    </div>
  );
}

// ─── Receive (QR) ────────────────────────────────────────────────────────
function WalletReceive({ networks }) {
  const live = networks.filter((n) => n.status === "live");
  const soon = networks.filter((n) => n.status !== "live");
  const [netCode, setNetCode] = useState("TRC20");
  const [addr, setAddr] = useState(null);
  const [notes, setNotes] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/wallet/receive`, { params: { network: netCode } })
      .then(({ data }) => { setAddr(data.address || null); setNotes(data.notes || data.message || ""); })
      .catch(() => {});
  }, [netCode]);

  const copy = () => {
    if (!addr) return;
    navigator.clipboard.writeText(addr);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="grid gap-5 md:grid-cols-2" data-testid="wallet-receive">
      <div className="rounded-2xl border border-white/10 bg-black/40 p-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Network</div>
        <div className="mt-3 flex flex-wrap gap-2">
          {live.map((n) => (
            <button
              key={n.code}
              onClick={() => setNetCode(n.code)}
              data-testid={`net-${n.code}`}
              className={`rounded-full px-4 py-2 text-xs uppercase tracking-[0.2em] ${netCode === n.code ? "bg-gold text-ink" : "border border-white/10 text-white/60 hover:border-gold"}`}
            >{n.label}</button>
          ))}
          {soon.map((n) => (
            <button key={n.code} onClick={() => setNetCode(n.code)} data-testid={`net-${n.code}`}
              className={`inline-flex items-center gap-1 rounded-full px-4 py-2 text-xs uppercase tracking-[0.2em] ${netCode === n.code ? "bg-amber-500/20 text-amber-200 border border-amber-500/40" : "border border-white/10 text-white/40 hover:border-white/30"}`}
            >
              <Lock className="h-3 w-3" /> {n.label} · Soon
            </button>
          ))}
        </div>
        <p className="mt-5 text-sm text-white/60">{notes}</p>
      </div>

      <div className="rounded-2xl border border-gold/40 bg-black/40 p-6 flex flex-col items-center text-center">
        {addr ? (
          <>
            <div className="rounded-xl bg-white p-4">
              <QRCodeCanvas value={addr} size={180} includeMargin={false} />
            </div>
            <div className="mt-5 w-full">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Your deposit address</div>
              <div className="mt-2 break-all font-mono text-sm text-white" data-testid="wallet-receive-addr">{addr}</div>
              <button onClick={copy} data-testid="wallet-receive-copy"
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2 text-xs uppercase tracking-[0.2em] text-gold hover:bg-gold hover:text-ink">
                {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
              </button>
            </div>
          </>
        ) : (
          <div className="flex h-full min-h-[260px] flex-col items-center justify-center text-white/50" data-testid="wallet-receive-soon">
            <Lock className="h-10 w-10 text-amber-400/70" />
            <div className="mt-4 text-sm">Coming soon</div>
            <div className="mt-1 text-xs text-white/40">Please use USDT TRC20 for now</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Send (USDT) ─────────────────────────────────────────────────────────
function WalletSend({ token, overview, onDone }) {
  const [addresses, setAddresses] = useState([]);
  const [destId, setDestId] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/wallet/addresses`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setAddresses(data.addresses || [])).catch(() => {});
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null); setBusy(true);
    try {
      const dest = addresses.find((a) => a.id === destId);
      if (!dest) throw new Error("Pick a destination address.");
      const amt = parseFloat(amount);
      if (!(amt > 0)) throw new Error("Amount must be greater than zero.");
      await axios.post(`${API}/terminal/withdraw`,
        { amount_usd: amt, destination_wallet: dest.address, network: dest.network },
        { headers: { Authorization: `Bearer ${token}` } });
      setMsg({ ok: true, text: `Withdrawal queued for admin review · $${amt.toFixed(2)} → ${dest.label}` });
      setAmount("");
      onDone?.();
    } catch (err) {
      const d = err.response?.data?.detail || err.message || "Could not submit send.";
      setMsg({ ok: false, text: typeof d === "string" ? d : "Could not submit send." });
    } finally {
      setBusy(false);
    }
  };

  if (addresses.length === 0) {
    return (
      <div className="rounded-2xl border border-white/10 bg-black/30 p-8 text-center" data-testid="wallet-send-empty">
        <div className="text-sm text-white/70">You haven't saved any payout address yet.</div>
        <div className="mt-1 text-xs text-white/40">Go to <strong>Addresses</strong> to add your Trust Wallet or Binance deposit address.</div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-black/40 p-6 space-y-4 max-w-xl" data-testid="wallet-send-form">
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Available: ${fmtUsd(overview?.balance_usd || 0)}</div>
      <div>
        <label className="text-xs uppercase tracking-[0.2em] text-white/50">Destination</label>
        <select value={destId} onChange={(e) => setDestId(e.target.value)}
          data-testid="wallet-send-destination"
          className="mt-2 w-full rounded-lg border border-white/15 bg-black/60 px-3 py-2.5 text-sm text-white">
          <option value="">— Choose a saved address —</option>
          {addresses.map((a) => (
            <option key={a.id} value={a.id}>{a.label} · {a.network} · {a.address.slice(0,8)}…{a.address.slice(-6)}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs uppercase tracking-[0.2em] text-white/50">Amount (USD)</label>
        <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" inputMode="decimal"
          data-testid="wallet-send-amount"
          className="mt-2 w-full rounded-lg border border-white/15 bg-black/60 px-3 py-2.5 text-sm text-white" />
      </div>
      {msg && (
        <div data-testid="wallet-send-msg" className={`rounded-lg border p-3 text-xs ${msg.ok ? "border-emerald-500/40 text-emerald-200" : "border-rose-500/40 text-rose-200"}`}>{msg.text}</div>
      )}
      <button disabled={busy} data-testid="wallet-send-submit"
        className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-xs uppercase tracking-[0.2em] text-ink disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Request withdrawal
      </button>
      <p className="text-[11px] text-white/40">All USDT withdrawals are released by Aureon admin during payout hours (10:00–18:00 SAST). You'll get a tx hash on completion.</p>
    </form>
  );
}

// ─── Saved Addresses ─────────────────────────────────────────────────────
function WalletAddresses({ token, networks, onChange }) {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [label, setLabel] = useState("");
  const [network, setNetwork] = useState("TRC20");
  const [address, setAddress] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/wallet/addresses`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setRows(data.addresses || [])).catch(() => {});
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      await axios.post(`${API}/wallet/addresses`,
        { label, network, address },
        { headers: { Authorization: `Bearer ${token}` } });
      setLabel(""); setAddress(""); setShowForm(false);
      load(); onChange?.();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not save address.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this address?")) return;
    await axios.delete(`${API}/wallet/addresses/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    load(); onChange?.();
  };

  const liveNets = networks.filter((n) => n.status === "live");

  return (
    <div data-testid="wallet-addresses">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-serif text-lg text-white">Saved payout addresses</h3>
        <button onClick={() => setShowForm((v) => !v)} data-testid="wallet-add-address"
          className="inline-flex items-center gap-1.5 rounded-full border border-gold/50 px-4 py-2 text-xs uppercase tracking-[0.2em] text-gold hover:bg-gold hover:text-ink">
          <Plus className="h-3.5 w-3.5" /> {showForm ? "Cancel" : "Add"}
        </button>
      </div>
      {showForm && (
        <form onSubmit={add} className="mb-5 rounded-2xl border border-white/10 bg-black/40 p-5 space-y-3 max-w-xl" data-testid="wallet-address-form">
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. My Trust Wallet, Binance deposit)"
            data-testid="wallet-address-label"
            className="w-full rounded-lg border border-white/15 bg-black/60 px-3 py-2.5 text-sm text-white" />
          <select value={network} onChange={(e) => setNetwork(e.target.value)}
            data-testid="wallet-address-network"
            className="w-full rounded-lg border border-white/15 bg-black/60 px-3 py-2.5 text-sm text-white">
            {liveNets.map((n) => <option key={n.code} value={n.code}>{n.label}</option>)}
          </select>
          <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="TRC-20 address (starts with T, 34 chars)"
            data-testid="wallet-address-value"
            className="w-full rounded-lg border border-white/15 bg-black/60 px-3 py-2.5 text-sm text-white font-mono" />
          {err && <div className="text-xs text-rose-300" data-testid="wallet-address-err">{err}</div>}
          <button disabled={busy} data-testid="wallet-address-save"
            className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2 text-xs uppercase tracking-[0.2em] text-ink disabled:opacity-50">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
          </button>
        </form>
      )}
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-black/30 p-6 text-center text-sm text-white/50" data-testid="wallet-addresses-empty">No saved addresses yet.</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-white/10 bg-black/40 p-4 flex items-start justify-between gap-3" data-testid={`wallet-address-row-${r.id}`}>
              <div className="min-w-0">
                <div className="font-serif text-base text-white">{r.label}</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold/80">{r.network}</div>
                <div className="mt-1 break-all font-mono text-xs text-white/60">{r.address}</div>
              </div>
              <button onClick={() => remove(r.id)} data-testid={`wallet-address-delete-${r.id}`}
                className="rounded-full p-2 text-white/40 hover:text-rose-300 hover:bg-white/5">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Bank Transfer ───────────────────────────────────────────────────────
function WalletBank({ token, overview, onChange }) {
  const [bens, setBens] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ label: "", bank_name: "", account_number: "", account_holder: "", swift_or_routing: "", country: "South Africa", reference: "" });
  const [benBusy, setBenBusy] = useState(false);
  const [benErr, setBenErr] = useState(null);
  const [reqBenId, setReqBenId] = useState("");
  const [reqAmount, setReqAmount] = useState("");
  const [reqMsg, setReqMsg] = useState(null);
  const [reqBusy, setReqBusy] = useState(false);

  const load = useCallback(() => {
    const h = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/beneficiaries`, { headers: h }).then(({ data }) => setBens(data.beneficiaries || [])).catch(() => {});
    axios.get(`${API}/bank-transfer/mine`, { headers: h }).then(({ data }) => setTransfers(data.transfers || [])).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const addBen = async (e) => {
    e.preventDefault(); setBenBusy(true); setBenErr(null);
    try {
      await axios.post(`${API}/beneficiaries`, form, { headers: { Authorization: `Bearer ${token}` } });
      setForm({ label: "", bank_name: "", account_number: "", account_holder: "", swift_or_routing: "", country: "South Africa", reference: "" });
      setShowAdd(false); load(); onChange?.();
    } catch (ex) {
      setBenErr(ex.response?.data?.detail || "Could not save beneficiary.");
    } finally { setBenBusy(false); }
  };

  const removeBen = async (id) => {
    if (!window.confirm("Remove this beneficiary?")) return;
    await axios.delete(`${API}/beneficiaries/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    load(); onChange?.();
  };

  const submitReq = async (e) => {
    e.preventDefault(); setReqBusy(true); setReqMsg(null);
    try {
      await axios.post(`${API}/bank-transfer/request`,
        { beneficiary_id: reqBenId, amount_usd: parseFloat(reqAmount) },
        { headers: { Authorization: `Bearer ${token}` } });
      setReqMsg({ ok: true, text: "Bank transfer requested · awaiting admin approval" });
      setReqAmount(""); load(); onChange?.();
    } catch (ex) {
      setReqMsg({ ok: false, text: ex.response?.data?.detail || "Could not submit transfer." });
    } finally { setReqBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="wallet-bank">
      {/* Beneficiaries */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-serif text-lg text-white">Beneficiaries</h3>
          <button onClick={() => setShowAdd((v) => !v)} data-testid="bank-add-beneficiary"
            className="inline-flex items-center gap-1.5 rounded-full border border-gold/50 px-4 py-2 text-xs uppercase tracking-[0.2em] text-gold hover:bg-gold hover:text-ink">
            <Plus className="h-3.5 w-3.5" /> {showAdd ? "Cancel" : "Add"}
          </button>
        </div>
        {showAdd && (
          <form onSubmit={addBen} className="mb-4 rounded-2xl border border-white/10 bg-black/40 p-5 grid gap-3 md:grid-cols-2" data-testid="bank-beneficiary-form">
            {[
              ["label", "Nickname (e.g. My FNB)"],
              ["account_holder", "Account holder name"],
              ["bank_name", "Bank name"],
              ["account_number", "Account number"],
              ["swift_or_routing", "SWIFT / routing (optional)"],
              ["country", "Country"],
              ["reference", "Default reference (optional)"],
            ].map(([k, ph]) => (
              <input key={k} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} placeholder={ph}
                data-testid={`bank-field-${k}`}
                className="rounded-lg border border-white/15 bg-black/60 px-3 py-2.5 text-sm text-white" />
            ))}
            {benErr && <div className="md:col-span-2 text-xs text-rose-300">{benErr}</div>}
            <button disabled={benBusy} data-testid="bank-save-beneficiary"
              className="md:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-ink disabled:opacity-50">
              {benBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save beneficiary
            </button>
          </form>
        )}
        {bens.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-black/30 p-6 text-center text-sm text-white/50" data-testid="bank-beneficiaries-empty">No beneficiaries yet.</div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {bens.map((b) => (
              <div key={b.id} className="rounded-xl border border-white/10 bg-black/40 p-4 flex items-start justify-between gap-3" data-testid={`bank-beneficiary-row-${b.id}`}>
                <div className="min-w-0">
                  <div className="font-serif text-base text-white">{b.label}</div>
                  <div className="text-xs text-white/60">{b.bank_name} · {b.country}</div>
                  <div className="mt-1 font-mono text-xs text-white/50">{b.account_number} · {b.account_holder}</div>
                  {b.swift_or_routing && <div className="text-[11px] text-white/40">SWIFT/Route: {b.swift_or_routing}</div>}
                </div>
                <button onClick={() => removeBen(b.id)} className="rounded-full p-2 text-white/40 hover:text-rose-300 hover:bg-white/5">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Request */}
      {bens.length > 0 && (
        <form onSubmit={submitReq} className="rounded-2xl border border-white/10 bg-black/40 p-5 space-y-3 max-w-xl" data-testid="bank-request-form">
          <h3 className="font-serif text-lg text-white">Request a bank transfer</h3>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Available: ${fmtUsd(overview?.balance_usd || 0)}</div>
          <select value={reqBenId} onChange={(e) => setReqBenId(e.target.value)} data-testid="bank-req-beneficiary"
            className="w-full rounded-lg border border-white/15 bg-black/60 px-3 py-2.5 text-sm text-white">
            <option value="">— Pick a beneficiary —</option>
            {bens.map((b) => <option key={b.id} value={b.id}>{b.label} · {b.bank_name}</option>)}
          </select>
          <input value={reqAmount} onChange={(e) => setReqAmount(e.target.value)} placeholder="Amount (USD)" inputMode="decimal"
            data-testid="bank-req-amount"
            className="w-full rounded-lg border border-white/15 bg-black/60 px-3 py-2.5 text-sm text-white" />
          {reqMsg && <div data-testid="bank-req-msg" className={`rounded-lg border p-3 text-xs ${reqMsg.ok ? "border-emerald-500/40 text-emerald-200" : "border-rose-500/40 text-rose-200"}`}>{reqMsg.text}</div>}
          <button disabled={reqBusy} data-testid="bank-req-submit"
            className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-xs uppercase tracking-[0.2em] text-ink disabled:opacity-50">
            {reqBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Submit request
          </button>
        </form>
      )}

      {/* Recent transfers */}
      <div>
        <h3 className="font-serif text-lg text-white mb-3">Recent transfers</h3>
        {transfers.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-black/30 p-6 text-center text-sm text-white/50" data-testid="bank-transfers-empty">No transfers yet.</div>
        ) : (
          <div className="space-y-2">
            {transfers.slice(0, 10).map((t) => (
              <div key={t.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-black/40 p-3" data-testid={`bank-transfer-${t.id}`}>
                <div>
                  <div className="font-serif text-base text-white">${fmtUsd(t.amount_usd)}</div>
                  <div className="text-xs text-white/50">{(t.beneficiary_snapshot || {}).bank_name} · {new Date(t.created_at).toLocaleString()}</div>
                </div>
                <StatusChip status={t.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
function StatusChip({ status }) {
  const map = {
    pending: "bg-amber-500/15 text-amber-200 border-amber-500/40",
    approved: "bg-sky-500/15 text-sky-200 border-sky-500/40",
    paid: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40",
    credited: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40",
    rejected: "bg-rose-500/15 text-rose-200 border-rose-500/40",
    queued: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  };
  const cls = map[status] || "bg-white/5 text-white/60 border-white/10";
  return <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${cls}`}>{status}</span>;
}

// ─── History ─────────────────────────────────────────────────────────────
function WalletHistory({ token, refreshKey }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get(`${API}/wallet/transactions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setRows(data.transactions || [])).catch(() => {});
  }, [token, refreshKey]);
  if (rows.length === 0) return <div className="rounded-2xl border border-white/10 bg-black/30 p-6 text-center text-sm text-white/50" data-testid="wallet-history-empty">No activity yet.</div>;
  return (
    <div className="space-y-2" data-testid="wallet-history">
      {rows.map((r) => (
        <div key={r.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-black/40 p-3" data-testid={`history-row-${r.id}`}>
          <div className="min-w-0">
            <div className="font-serif text-base text-white">{labelFor(r.kind)} · ${fmtUsd(r.amount_usd)}</div>
            <div className="text-xs text-white/50">{r.destination || "—"} · {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</div>
            {r.tx_hash && <div className="font-mono text-[11px] text-gold/70 truncate">{r.tx_hash}</div>}
            {r.ref_number && <div className="font-mono text-[11px] text-gold/70">Ref: {r.ref_number}</div>}
          </div>
          <StatusChip status={r.status} />
        </div>
      ))}
    </div>
  );
}
function labelFor(kind) {
  return { usdt_withdrawal: "USDT withdrawal", bank_transfer: "Bank transfer", crypto_deposit: "USDT deposit" }[kind] || kind;
}

// ─── Cards ───────────────────────────────────────────────────────────────
function WalletCards({ overview, account, profile }) {
  const tiers = overview?.tiers || [];
  const current = tiers.findIndex((t) => t.slug === overview?.tier_slug);
  const holder = profile?.fullName || overview?.holder_name;
  const balance = profile?.accountBalance ?? account?.equity_usd ?? overview?.balance_usd;
  const currentPrice = profile?.accountTier || tiers[current]?.price || 100;
  return (
    <div data-testid="wallet-cards">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-serif text-lg text-white">Aureon Tier Cards</h3>
          <div className="text-xs text-white/50" data-testid="wallet-card-holder">{holder || "—"} · {overview?.code || account?.code}</div>
        </div>
        <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-amber-200" data-testid="wallet-cards-demo-badge">Demo · simulated balance</span>
      </div>
      <div className="max-w-[420px]" data-testid="wallet-current-card">
        <VirtualCard clientName={holder} balance={balance} tier={currentPrice} accessCode={profile?.accessCode || account?.code} />
      </div>
      <h4 className="mt-8 mb-3 text-[10px] uppercase tracking-[0.3em] text-white/40">All tiers</h4>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {tiers.filter((t) => t.price !== currentPrice).map((t) => (
          <VirtualCard key={t.slug} preview={t.price < currentPrice ? "achieved" : "locked"}
            clientName={holder} tier={t.price} accessCode={account?.code} />
        ))}
      </div>
      <p className="mt-4 text-xs text-white/40">Your card mirrors the balance shown in the terminal. Higher tiers unlock once your account reaches them.</p>
    </div>
  );
}
